// Every request this app makes to its own API says which build sent it,
// in an X-App-Build header (the bundle's 12-character commit, baked in at
// build time as REACT_APP_BUILD_VERSION). Phones keep old bundles for
// days, so the server records which builds are still calling it
// (server/middleware/appBuild.js), and a change that retires an old
// request shape waits until the builds that send it have gone quiet
// (price board plan, F6).
//
// Imported first in src/index.js, for its side effect: it sets axios's
// default header and wraps window.fetch before any other module runs, so
// every axios instance created later and every raw fetch carry it. Only
// requests to this app's own /api get the header; anything cross-origin
// is passed through untouched.
import axios from 'axios';

export const APP_BUILD = (process.env.REACT_APP_BUILD_VERSION || '').trim() || 'dev';
export const APP_BUILD_HEADER = 'X-App-Build';

// The API's origin: same origin in production, :5000 on a laptop.
function apiOrigins(win) {
  const out = new Set();
  try { out.add(win.location.origin); } catch (_) { /* no location */ }
  const configured = process.env.REACT_APP_API_URL;
  if (configured) {
    try { out.add(new URL(configured, win.location.href).origin); } catch (_) { /* bad URL */ }
  }
  try {
    if (win.location.hostname === 'localhost') out.add('http://localhost:5000');
  } catch (_) { /* no location */ }
  return out;
}

// True when `input` (a URL string, URL or Request) is a call to this
// app's own /api.
export function isOwnApi(input, win = window) {
  let raw;
  if (typeof input === 'string') raw = input;
  else if (input && typeof input.url === 'string') raw = input.url;
  else if (input && typeof input.href === 'string') raw = input.href;
  else return false;
  let url;
  try { url = new URL(raw, win.location.href); } catch (_) { return false; }
  return apiOrigins(win).has(url.origin) && url.pathname.split('/')[1] === 'api';
}

export function installAppBuildHeader({ ax = axios, win = typeof window !== 'undefined' ? window : null } = {}) {
  if (ax && ax.defaults && ax.defaults.headers) {
    ax.defaults.headers.common = ax.defaults.headers.common || {};
    ax.defaults.headers.common[APP_BUILD_HEADER] = APP_BUILD;
  }
  if (!win || typeof win.fetch !== 'function' || win.fetch.__appBuild) return;
  const original = win.fetch.bind(win);
  const wrapped = (input, init) => {
    if (!isOwnApi(input, win)) return original(input, init);
    const headers = new win.Headers((init && init.headers) || (input && input.headers) || undefined);
    if (!headers.has(APP_BUILD_HEADER)) headers.set(APP_BUILD_HEADER, APP_BUILD);
    return original(input, { ...(init || {}), headers });
  };
  wrapped.__appBuild = true;
  win.fetch = wrapped;
}

installAppBuildHeader();
